import clsx from 'clsx'
import { rem } from 'polished'
import { useState } from 'react'
import * as React from 'react'
import styled, { keyframes } from 'styled-components'

import StickyWithStatus from 'components/Common/StickyWithStatus'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import zIndex from 'styles/tools/z-index'
import type { ItinerarySection } from 'tripPlanner/utils/itinerary'
import { SUGGESTED } from 'tripPlanner/utils/itinerary'
import { getThemeClassName } from 'lib/theme/themeUtils'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const HeaderStickyContainer = styled(StickyWithStatus)`
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }

  @media print {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.four};
    border-bottom: 1px solid
      ${props => props.theme.palette.neutral.default.four};
  }

  &.withShadow {
    z-index: ${zIndex.header - 20};
  }

  &.hide {
    display: none;
  }

  z-index: 1;
`

const Root = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: box-shadow 0.3s;
  padding: ${rem(16)} ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)} ${rem(40)};
  }

  &.suggested {
    background-color: ${props => props.theme.palette.highlight.secondary.normalBackground};
  }

  &.withShadow {
    box-shadow: ${props => props.theme.shadow.bottom.medium};
  }
`

const Label = styled(BodyText)`
  flex: 1;
`

const progressAnimation = keyframes`
  0% {
    width: 0%;
  }

  60% {
    width: 80%;
  }

  85% {
    width: 90%;
  }

  100% {
    width: 95%;
  }
`

const ProgressBarContainer = styled.div`
  width: 100%;
  height: ${rem(8)};
  background-color: ${props => props.theme.palette.neutral.default.three};
  overflow: hidden;
`

const ProgressBarFill = styled.div`
  height: 100%;
  width: 0%;
  background-color: ${props => props.theme.palette.highlight.primary.normalForeground};
  animation: ${progressAnimation} 70s cubic-bezier(0.4, 0, 0.2, 1) forwards;
`

interface Props {
  title: string
  subtitle?: string
  section: ItinerarySection
  top: React.ComponentProps<typeof HeaderStickyContainer>['top']
  endContent?: React.ReactNode
  isRegenerating?: boolean
}

function SectionHeading({ title, subtitle, section, top, endContent, isRegenerating }: Props) {
  const [filtersAreStuck, setFiltersAreStuck] = useState(false)

  return (
    <Group direction="vertical">
      <HeaderStickyContainer
        onStuckChange={setFiltersAreStuck}
        className={clsx({ withShadow: filtersAreStuck })}
        top={top}
      >
        <Root className={clsx({ withShadow: filtersAreStuck, suggested: section === SUGGESTED }, getThemeClassName({ mobile: 'inverse' }))}>
          <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
            <VerticalSpacer gap={2}>
              <Label variant="large" colour="highlight-secondary-contrast">
                <strong>
                  {title}
                </strong>
                {subtitle && <>&nbsp;&nbsp;{subtitle}</>}
              </Label>
            </VerticalSpacer>
            {endContent}
          </Group>
        </Root>
      </HeaderStickyContainer>
      {isRegenerating && (
        <ProgressBarContainer>
          <ProgressBarFill />
        </ProgressBarContainer>
      )}
    </Group>
  )
}

export default SectionHeading
